import { useEffect, useState } from 'react';
import {
  json,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
  useLoaderData,
} from '@remix-run/react';
import './tailwind.css';
import { GetInTouchModal } from './components/get-in-touch/get-in-touch-modal';
import { useSnapshot } from 'valtio';
import { inquiryModal, onClose } from './state/inquiry-modal';
import './styles.css';
import { useChangeLanguage } from 'remix-i18next/react';
import i18nextServer from './localization/i18next.server';
import { LoaderFunctionArgs, TypedResponse } from '@remix-run/cloudflare';
import { useTranslation } from 'react-i18next';
import { Snackbar } from './components/snackbar/snackbar';
import { propagateError } from './propagate-error';
import { FACEBOOK, INSTAGRAM, TWITTER, YOUTUBE } from './components/socials';
import Sprite from './sprites/sprite';
import { ApplicationEnvironment } from './environment';
import { getOrigin } from './get-origin';
import { IntercomProvider } from 'react-use-intercom';
import { NivaasOffering } from './types/offerings';
import { ExchangeRate, exchangeRate } from './state/prices';
import { environmentState } from './state/environment';
import { Countries, Currencies } from 'country-to-currency';
import {
  initializeOfferings as initializeSharedOfferings,
  initializeAnalytics,
  initializeEnvironment,
  initializePackages,
  reservationStore as sharedReservationStore,
  exchangeRate as sharedExchangeRate,
  type BrandConfig,
  type NivaasPackage,
} from '@kumbh/booking-components';
import { useInitializeAmplitude } from './hooks/use-initialize-amplitude';
import { analyticsState } from './state/amplitude';

/**
 * Response from the offerings API endpoint
 */
interface OfferingsResponse {
  offerings: NivaasOffering[];
}

/**
 * Response from the packages API endpoint
 */
interface PackagesResponse {
  packages: NivaasPackage[];
}

interface LoaderData {
  locale: string;
  intercomKey: string;
  amplitudeKey: string;
  offerings: NivaasOffering[];
  packages: NivaasPackage[];
  prices: ExchangeRate;
  countryCode: string;
  apiUrl: string;
  paypalClientId: string;
}

export const loader = async ({
  request,
  context,
}: LoaderFunctionArgs): Promise<TypedResponse<LoaderData>> => {
  const env = context.cloudflare.env as ApplicationEnvironment;
  const requestCookie = request.headers.get('cookie') ?? '';
  const locale = await i18nextServer.getLocale(request);
  const intercomKey = env.INTERCOM_APP_KEY;
  const amplitudeKey = env.AMPLITUDE_API_KEY;

  const country = context.cloudflare.cf.country;

  try {
    // Fetch session and offerings in parallel
    const sessionResponse = await fetch(`${env.API_URL}/api/session`, {
      headers: {
        origin: getOrigin(request),
        Cookie: requestCookie,
        'X-User-Country': country || '',
      },
    });
    const cookie = sessionResponse.headers.get('set-cookie');

    if (!sessionResponse.ok || !cookie) {
      return json({
        locale,
        intercomKey,
        amplitudeKey,
        offerings: [],
        packages: [],
        prices: { rate: 1, symbol: '₹', currency: 'INR' as Currencies },
        countryCode: country ?? 'IN',
        apiUrl: env.API_URL,
        paypalClientId: env.PAYPAL_CLIENT_ID,
      });
    }
    const countryCode =
      country ?? (sessionResponse.headers.get('X-Country') as Countries) ?? 'IN';

    const prices = await sessionResponse.json<ExchangeRate>();

    const offeringsResponse = await fetch(`${env.API_URL}/api/offerings`, {
      headers: {
        origin: getOrigin(request),
        Cookie: cookie,
      },
    });

    if (!offeringsResponse.ok) {
      return json(
        {
          locale,
          intercomKey,
          amplitudeKey,
          offerings: [],
          packages: [],
          prices: { rate: 1, symbol: '₹', currency: 'INR' as Currencies },
          countryCode,
          apiUrl: env.API_URL,
          paypalClientId: env.PAYPAL_CLIENT_ID,
        },
        {
          headers: {
            'X-Country': country ?? 'IN',
            'Set-Cookie': cookie,
          },
        },
      );
    }

    // Parse offerings response with proper typing
    let offeringsData: NivaasOffering[] = [];
    try {
      const parsed = (await offeringsResponse.json()) as OfferingsResponse;
      // Validate that we got an array
      if (Array.isArray(parsed.offerings)) {
        offeringsData = parsed.offerings;
      }
    } catch (e) {
      console.error('Failed to parse offerings response:', e);
    }

    // Fetch packages
    let packagesData: NivaasPackage[] = [];
    try {
      const packagesResponse = await fetch(`${env.API_URL}/api/packages`, {
        headers: {
          origin: getOrigin(request),
          Cookie: cookie,
        },
      });
      if (packagesResponse.ok) {
        const parsed = (await packagesResponse.json()) as PackagesResponse;
        if (Array.isArray(parsed.packages)) {
          packagesData = parsed.packages;
        }
      }
    } catch (e) {
      console.error('Failed to fetch packages:', e);
    }

    return json(
      {
        locale,
        intercomKey,
        amplitudeKey,
        offerings: offeringsData,
        packages: packagesData,
        prices,
        countryCode,
        apiUrl: env.API_URL,
        paypalClientId: env.PAYPAL_CLIENT_ID,
      },
      {
        headers: {
          'Set-Cookie': cookie,
          'X-Country': countryCode,
        },
      },
    );
  } catch (err) {
    propagateError(err, 'Failed to get session');
    return json({
      locale,
      intercomKey,
      amplitudeKey,
      offerings: [],
      packages: [],
      prices: { rate: 1, symbol: '₹', currency: 'INR' as Currencies },
      countryCode: country ?? 'IN',
      apiUrl: env.API_URL,
      paypalClientId: env.PAYPAL_CLIENT_ID,
    });
  }
};

export function Layout({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const data = useRouteLoaderData<typeof loader>('root');

  const locale = data?.locale ?? 'en';
  useChangeLanguage(locale);

  return (
    <html lang={locale} dir={i18n.dir()}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://widget.intercom.io" />
        <link rel="preconnect" href="https://js.intercomcdn.com" />
        <Meta />
        <Links />
      </head>
      <body>
        <Snackbar />
        <IntercomProvider
          appId={data?.intercomKey ?? ''}
          initializeDelay={2_000}
          autoBoot={true}
        >
          {children}
        </IntercomProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const data = useLoaderData<typeof loader>();
  const { isOpen } = useSnapshot(inquiryModal);

  // Initialize Amplitude analytics
  useInitializeAmplitude(data.amplitudeKey);

  // Initialize local environment state
  environmentState.apiUrl = data.apiUrl;
  environmentState.countryCode = data.countryCode;

  // Initialize exchange rate for local prices
  if (data?.prices?.currency) {
    exchangeRate.currency = data.prices.currency;
    exchangeRate.rate = data.prices.rate;
    exchangeRate.symbol = data.prices.symbol;

    sharedExchangeRate.currency = data.prices.currency;
    sharedExchangeRate.rate = data.prices.rate;
    sharedExchangeRate.symbol = data.prices.symbol;
  }

  // Initialize shared environment state (used by shared booking components)
  initializeEnvironment({
    apiUrl: data.apiUrl,
    paypalClientId: data.paypalClientId,
    countryCode: data.countryCode,
  });

  // Initialize shared analytics with Amplitude
  initializeAnalytics(
    {
      track: (event, eventData) => {
        analyticsState.amplitude?.track(event, eventData);
      },
    },
    'kumbh-sukrit',
  );

  // Configure brand for shared reservation store (Magh Mela 2026 dates)
  const brandConfig: BrandConfig = {
    brandId: 'kumbh-sukrit',
    offeringStartsFrom: new Date('2025-12-31'), // Paush Purnima
    offeringEndsAt: new Date('2026-02-20'), // Maha Shivratri
  };
  sharedReservationStore.setBrandConfig(brandConfig);
  sharedReservationStore.initializeCountryCode(data.countryCode);

  // Initialize shared offerings state AFTER brand config
  // This ensures the reservation store recomputes prices with the new offerings
  if (data.offerings && data.offerings.length > 0) {
    initializeSharedOfferings(data.offerings);
    sharedReservationStore.refreshFromOfferings();
  }

  // Initialize packages state for shared booking components
  if (data.packages && data.packages.length > 0) {
    initializePackages(data.packages);
  }

  return (
    <>
      <Outlet />
      <GetInTouchModal isOpen={isOpen} onClose={onClose} />
      <Socials />
    </>
  );
}

const Socials = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past ~80% of viewport height (past header)
      const scrollThreshold = window.innerHeight * 0.8;
      setIsVisible(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="flex md:hidden flex-col fixed right-0 top-1/2 -translate-y-1/2 bg-white rounded-l-lg px-2 py-3 shadow-lg z-20 animate-in fade-in slide-in-from-right duration-300">
      <ul className="flex flex-col list-none gap-4 pt-1 xs:pt-0 items-center">
        <li>
          <a href={FACEBOOK} target="_blank" rel="noreferrer">
            <Sprite
              name="facebook"
              width="64"
              height="64"
              className="min-w-6 w-6 min-h-6 h-6"
            />
          </a>
        </li>
        <li>
          <a href={INSTAGRAM} target="_blank" rel="noreferrer">
            <img
              src="/instagram.png"
              alt="Instagram"
              className="min-w-6 w-6 min-h-6 h-6"
            />
          </a>
        </li>
        <li>
          <a href={TWITTER} target="_blank" rel="noreferrer">
            <Sprite
              name="twitter"
              width="64"
              height="64"
              className="min-w-6 w-6 min-h-6 h-6"
            />
          </a>
        </li>
        <li>
          <a href={YOUTUBE} target="_blank" rel="noreferrer">
            <Sprite
              name="youtube"
              width="64"
              height="45"
              className="min-w-6 w-6 min-h-6 h-6"
            />
          </a>
        </li>
      </ul>
    </div>
  );
};
